// Typed client for the Workers API. Shapes come from @wedding/shared.
import type {
  UploadInitRequest,
  UploadInitResponse,
  UploadConfirmRequest,
  UploadConfirmResponse,
  MomentsResponse,
  MomentPhotosResponse,
  GalleryCountsResponse,
  TextSearchRequest,
  TextSearchResponse,
  FaceSearchRequest,
  FaceSearchResponse,
  SelfieUrlRequest,
  SelfieUrlResponse,
  PublicKeepConfig,
} from "@wedding/shared";
import { API_BASE } from "./config";

export type KeepInfo = PublicKeepConfig;

// Surface the server's {error} message when present, so failures are diagnosable
// (e.g. "File too large" instead of a bare "400").
async function readError(res: Response, method: string, path: string): Promise<Error> {
  let detail = "";
  try {
    const data = (await res.json()) as { error?: string };
    if (data?.error) detail = `: ${data.error}`;
  } catch {
    // non-JSON body — ignore
  }
  return new Error(`${method} ${path} -> ${res.status}${detail}`);
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) throw await readError(res, "GET", path);
  return res.json() as Promise<T>;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await readError(res, "POST", path);
  return res.json() as Promise<T>;
}

export const api = {
  resolveKeep: (slug: string) => get<KeepInfo>(`/api/keeps/${encodeURIComponent(slug)}`),
  resolveKeepByHost: (host: string) => get<KeepInfo>(`/api/keeps/resolve?host=${encodeURIComponent(host)}`),

  galleryCounts: (keepId: string) =>
    get<GalleryCountsResponse>(`/api/gallery/counts?keepId=${keepId}`),

  recentPhotos: (keepId: string, limit = 12) =>
    get<{ photos: import("@wedding/shared").PhotoDTO[] }>(`/api/gallery/recent?keepId=${keepId}&limit=${limit}`),

  allPhotos: (keepId: string, cursor?: string) =>
    get<MomentPhotosResponse>(`/api/gallery/all?keepId=${keepId}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`),

  moments: (keepId: string) => get<MomentsResponse>(`/api/moments?keepId=${keepId}`),

  momentPhotos: (keepId: string, key: string, cursor?: string) =>
    get<MomentPhotosResponse>(
      `/api/moments/${encodeURIComponent(key)}/photos?keepId=${keepId}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),

  uploadInit: (req: UploadInitRequest) => post<UploadInitResponse>("/api/uploads/init", req),
  uploadConfirm: (req: UploadConfirmRequest) => post<UploadConfirmResponse>("/api/uploads/confirm", req),

  searchText: (req: TextSearchRequest) => post<TextSearchResponse>("/api/search/text", req),
  selfieUrl: (req: SelfieUrlRequest) => post<SelfieUrlResponse>("/api/search/selfie-url", req),
  searchFace: (req: FaceSearchRequest) => post<FaceSearchResponse>("/api/search/face", req),
};

/** Upload one file's bytes directly to R2 using the presigned PUT URL + exact headers.
 * Retries transient failures; surfaces the real error (HTTP status vs network/CORS). */
export async function putToR2(uploadUrl: string, headers: Record<string, string>, file: Blob): Promise<void> {
  let lastErr = "";
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await fetch(uploadUrl, { method: "PUT", headers, body: file });
      if (res.ok) return;
      lastErr = `R2 responded ${res.status}`;
      // 4xx (e.g. signature/expiry) won't fix on retry; bail.
      if (res.status >= 400 && res.status < 500) break;
    } catch (e) {
      // A thrown fetch == network drop or CORS rejection (no HTTP status available).
      lastErr = `network/CORS error: ${(e as Error).message}`;
    }
    await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
  }
  throw new Error(lastErr || "upload failed");
}
